import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  LayoutDashboard, Building2, Hospital, ShoppingCart, FileText,
  ClipboardList, Boxes, IndianRupee, Trophy, BarChart3, Download, Search, Languages, Settings, Truck, RotateCcw,
  LogOut, ShieldCheck, FileSignature, Activity, PanelLeftClose, PanelLeftOpen, Command, BookOpen,
} from "lucide-react";
import { useLang } from "../i18n";
import api from "../api";
import { useAuth, can } from "../context/AuthContext";
import { CompanyMark, companyBrand } from "../branding/companyBrand";

const LOGIN_ACTIVITY_OWNER = "r.pandey@shreegauripharma.in";

const navGroups = [
  { label: null, items: [
    { to: "/", key: "dashboard", icon: LayoutDashboard, end: true, module: "dashboard" },
  ] },
  { label: "Quotation", items: [
    { to: "/quotations", key: "quotations", icon: ClipboardList, module: "quotation" },
    { to: "/parties/institution", key: "organisations", icon: Hospital, module: "masters" },
  ] },
  { label: "Purchase", items: [
    { to: "/purchases", key: "purchases", icon: ShoppingCart, module: "purchase" },
    { to: "/parties/supplier", key: "suppliers", icon: Building2, module: "masters" },
  ] },
  { label: "Sales", items: [
    { to: "/sales", key: "sales", icon: FileText, module: "invoice" },
    { to: "/parties/customer", key: "customers", icon: Hospital, module: "masters" },
  ] },
  { label: "Stock", items: [
    { to: "/stock", key: "stockItems", icon: Boxes, module: "stock" },
  ] },
  { label: "Letters", items: [
    { to: "/letters", key: "letters", icon: FileSignature, module: "documents" },
  ] },
  { label: "Documents", items: [
    { to: "/proformas", key: "proformas", icon: FileText, module: "invoice" },
    { to: "/challans", key: "challans", icon: Truck, module: "invoice" },
    { to: "/eway", key: "eway", icon: Truck, module: "invoice" },
    { to: "/returns", key: "returns", icon: RotateCcw, module: "invoice" },
  ] },
  { label: "Tenders", items: [
    { to: "/tenders/dashboard", key: "tenderDashboard", icon: LayoutDashboard, module: "tender" },
    { to: "/tenders", key: "allTenders", icon: Trophy, end: true, module: "tender" },
    { to: "/tenders/security", key: "emdSecurity", icon: IndianRupee, module: "tender" },
    { to: "/tenders/documents", key: "tenderDocuments", icon: FileText, module: "tender" },
    { to: "/tenders/contracts", key: "rateContracts", icon: ClipboardList, module: "tender" },
    { to: "/tenders/reports", key: "tenderReports", icon: BarChart3, module: "tender" },
  ] },
  { label: "Money", items: [
    { to: "/finance", key: "finance", icon: IndianRupee, module: "accounts" },
    { to: "/payments", key: "payments", icon: IndianRupee, module: "accounts" },
    { to: "/reports", key: "reports", icon: BarChart3, module: "accounts" },
  ] },
  { label: "Private", items: [
    { to: "/login-activity", key: "loginActivity", icon: Activity, ownerOnly: true },
  ] },
  { label: null, items: [
    { to: "/settings", key: "settings", icon: Settings, module: "admin" },
    { to: "/admin", key: "admin", icon: ShieldCheck, module: "admin" },
  ] },
];

const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const nav = useNavigate();
  const { t } = useLang();

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        ref.current?.focus();
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    if (q.length < 2) { setRes(null); return; }
    const id = setTimeout(async () => {
      try { setRes((await api.get("/search", { params: { q } })).data); } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  const go = (path) => { setOpen(false); setQ(""); nav(path); };
  const Section = ({ title, rows, render, path }) =>
    rows?.length ? (
      <div className="py-1">
        <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">{title}</p>
        {rows.map((r) => (
          <button key={r.id} data-testid={`search-result-${r.id}`} onClick={() => go(path(r))}
            className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-teal-50/70 text-slate-700 transition-colors">
            {render(r)}
          </button>
        ))}
      </div>
    ) : null;

  return (
    <div className="relative w-full max-w-xl">
      <div className="relative">
        <Search className="absolute left-3.5 top-3 h-4 w-4 text-slate-400" />
        <input ref={ref} data-testid="global-search-input" value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={t("search")}
          className="sgp-input pl-10 pr-16 bg-slate-50/80 border-slate-200" />
        <span className="pointer-events-none absolute right-3 top-2.5 inline-flex h-5 items-center gap-1 rounded border border-slate-200 bg-white px-1.5 text-[10px] font-semibold text-slate-400 shadow-sm">
          <Command className="h-3 w-3" /> K
        </span>
      </div>
      <AnimatePresence>
        {open && res && (
          <motion.div data-testid="global-search-results"
            initial={{ opacity: 0, y: -4, scale: 0.99 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -4, scale: 0.99 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
            className="absolute mt-2 w-full bg-white/98 border border-slate-200 rounded-xl shadow-[0_18px_60px_rgba(15,23,42,0.14)] z-50 max-h-96 overflow-auto p-1.5">
            <Section title="Medicines" rows={res.items} render={(r) => `${r.name} — ${r.hsn}`} path={() => "/items"} />
            <Section title="Companies" rows={res.companies} render={(r) => r.name} path={() => "/companies"} />
            <Section title="Hospitals" rows={res.hospitals} render={(r) => r.name} path={() => "/hospitals"} />
            <Section title="Sales Bills" rows={res.sales} render={(r) => `${r.bill_no} — ${r.hospital_name}`} path={(r) => `/print/sale/${r.id}`} />
            <Section title="Purchase Bills" rows={res.purchases} render={(r) => `${r.bill_no} — ${r.company_name}`} path={() => "/purchases"} />
            <Section title="Tenders" rows={res.tenders} render={(r) => r.tender_no} path={() => "/tenders"} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Layout() {
  const { lang, toggle, t } = useLang();
  const { user, logout, workspace } = useAuth();
  const [profile, setProfile] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("sgp-sidebar-collapsed") === "1"; } catch { return false; }
  });
  const navigate = useNavigate();
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const companyId = workspace?.company?.id || "sgp";
  const brand = companyBrand(workspace?.company || { id: companyId });
  const shell = brand.shell;
  const sidebarWidth = collapsed ? 80 : shell === "prachi" ? 288 : shell === "neo" ? 248 : 272;
  const visualMap = {
    heritage: {
      aside: "bg-[#1b2940] text-slate-50 border-[#34445f]",
      muted: "text-slate-300/85",
      navIdle: "text-slate-200/90 hover:bg-[#2a3a55] hover:text-white",
      navActive: "text-white",
      accent: "text-gold-400",
      accentBar: "bg-gold-400",
      divider: "border-[#3a4b67]/80",
      header: "bg-white/92",
      action: "bg-[#30415f] hover:bg-[#3a4e70] text-slate-100",
    },
    prachi: {
      aside: "bg-[#172b3a] text-white border-[#354f63]",
      muted: "text-slate-300/80",
      navIdle: "text-slate-200/90 hover:bg-white/10 hover:text-white",
      navActive: "text-white",
      accent: "text-sky-200",
      accentBar: "bg-sky-300",
      divider: "border-[#354f63]",
      header: "bg-[#f8fafc]/95",
      action: "bg-[#315f7d] hover:bg-[#24485f] text-white",
    },
    neo: {
      aside: "bg-[#f6fffc] text-emerald-950 border-emerald-200",
      muted: "text-emerald-700/70",
      navIdle: "text-emerald-900/70 hover:bg-emerald-100/70 hover:text-emerald-950",
      navActive: "text-emerald-950",
      accent: "text-cyan-700",
      accentBar: "bg-cyan-600",
      divider: "border-emerald-200",
      header: "bg-white/95",
      action: "bg-emerald-950 hover:bg-emerald-900 text-emerald-50",
    },
  };
  const visual = visualMap[shell] || visualMap.heritage;

  useEffect(() => {
    document.documentElement.dataset.sgpCompany = companyId;
    return () => { delete document.documentElement.dataset.sgpCompany; };
  }, [companyId]);

  useEffect(() => {
    setProfile(null);
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
  }, [companyId, workspace?.financial_year]);

  const visibleGroups = navGroups
    .map((g) => ({
      ...g,
      items: g.items.filter((item) => item.ownerOnly
        ? user?.email?.toLowerCase() === LOGIN_ACTIVITY_OWNER
        : can(user, item.module, "view")),
    }))
    .filter((g) => g.items.length);

  const toggleSidebar = () => {
    setCollapsed((current) => {
      const next = !current;
      try { localStorage.setItem("sgp-sidebar-collapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const doExport = async () => {
    const r = await api.get("/export", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url;
    const code = workspace?.company?.short_name || "SGP";
    a.download = `${code}_${workspace?.financial_year || "workspace"}_backup_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={`min-h-screen ${shell === "neo" ? "bg-[#eafbf6]" : shell === "prachi" ? "bg-[#f1f5f9]" : "bg-slate-50/70"}`}>
      <aside data-testid="sidebar"
        style={{ width: sidebarWidth, transition: reduceMotion ? "none" : "width 180ms cubic-bezier(.2,.8,.2,1)" }}
        className={`shrink-0 ${visual.aside} flex flex-col fixed inset-y-0 z-40 border-r shadow-[8px_0_32px_rgba(15,23,42,0.08)]`}>
        <div className={`h-[72px] px-4 border-b ${visual.divider} flex items-center`}>
          <div className={`flex items-center w-full ${collapsed ? "justify-center" : "gap-3"}`}>
            <CompanyMark
              company={workspace?.company || { id: companyId, name: "Shree Gauri Pharma", short_name: "SGP" }}
              className="h-12 w-12"
              imageClassName="h-11 w-11"
            />
            {!collapsed && (
              <div className="min-w-0 flex-1">
                <p className="font-heading font-bold leading-tight text-sm">{workspace?.company?.name || "Company"}</p>
                <p className={`text-[10px] uppercase tracking-[0.08em] ${visual.accent}`}>{workspace?.financial_year} · SGP Office OS</p>
              </div>
            )}
            {!collapsed && (
              <button data-testid="sidebar-toggle" onClick={toggleSidebar} title="Collapse sidebar"
                className={`ml-auto inline-flex h-8 w-8 items-center justify-center rounded-lg ${visual.muted} hover:bg-black/10 transition-colors`}>
                <PanelLeftClose className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {collapsed && (
          <div className="px-3 pt-3">
            <button data-testid="sidebar-toggle" onClick={toggleSidebar} title="Expand sidebar"
              className={`w-full inline-flex h-9 items-center justify-center rounded-lg ${visual.muted} hover:bg-black/10 transition-colors`}>
              <PanelLeftOpen className="h-4 w-4" />
            </button>
          </div>
        )}

        <nav className={`flex-1 overflow-y-auto py-3 ${collapsed ? "px-2 space-y-2.5" : "px-2.5 space-y-3.5"}`}>
          {visibleGroups.map((group, groupIndex) => (
            <div key={group.label || `overview-${groupIndex}`}>
              {group.label && !collapsed && (
                <p className={`px-3 pb-1.5 text-[9px] font-bold uppercase tracking-[0.18em] ${shell === "heritage" ? "text-slate-400" : "text-slate-500"}`}>{group.label}</p>
              )}
              {group.label && collapsed && groupIndex > 0 && <div className={`mx-2 my-2 border-t ${visual.divider}`} />}
              <div className="space-y-1">
                {group.items.map(({ to, key, icon: Icon, end }) => {
                  const isActive = end ? location.pathname === to : location.pathname.startsWith(to);
                  return (
                    <NavLink key={key} to={to} end={end} data-testid={`nav-${key}`} title={collapsed ? t(key) : undefined}
                      className={`relative flex items-center ${shell === "prachi" ? "rounded-2xl" : shell === "neo" ? "rounded-xl" : "rounded-lg"} text-sm transition-colors ${collapsed ? "h-10 justify-center px-2" : "gap-3 px-3 py-2.5"} ${isActive ? visual.navActive : visual.navIdle}`}>
                      {isActive && (
                        <motion.div layoutId="nav-active-pill" transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 430, damping: 36 }}
                          className={`absolute inset-0 rounded-lg ${shell === "neo" ? "bg-emerald-100/80 ring-emerald-200" : "bg-white/[0.07] ring-white/[0.08]"} ring-1 ring-inset`} />
                      )}
                      {isActive && !collapsed && <span className={`absolute left-0 top-2 bottom-2 w-0.5 rounded-full ${visual.accentBar}`} />}
                      <Icon className={`relative h-4 w-4 shrink-0 ${isActive ? visual.accent : visual.muted}`} />
                      {!collapsed && <span className={`relative truncate ${isActive ? "font-semibold" : ""}`}>{t(key)}</span>}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className={`p-3 border-t ${visual.divider} space-y-2.5`}>
          {can(user, "admin", "full") && (
            <button data-testid="export-backup-btn" onClick={doExport} title={collapsed ? t("export") : undefined}
              className={`w-full flex items-center justify-center gap-2 text-xs px-3 py-2 rounded-lg ${visual.action} transition-colors ${collapsed ? "px-0" : ""}`}>
              <Download className="h-3.5 w-3.5" /> {!collapsed && t("export")}
            </button>
          )}
          <div className={`flex items-center gap-2 ${collapsed ? "justify-center" : "justify-between px-1"}`}>
            {!collapsed && (
              <div className="min-w-0">
                <p className={`text-xs font-semibold truncate ${shell === "heritage" ? "text-slate-100" : "text-slate-200"}`}>{user?.name || user?.email}</p>
                <p className={`text-[10px] truncate ${shell === "heritage" ? "text-slate-400" : "text-slate-500"}`}>{user?.role}</p>
              </div>
            )}
            <button data-testid="logout-btn" onClick={doLogout} title="Sign out"
              className={`shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-lg ${visual.muted} hover:bg-black/10 transition-colors`}>
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      <div style={{ marginLeft: sidebarWidth, transition: reduceMotion ? "none" : "margin-left 180ms cubic-bezier(.2,.8,.2,1)" }}
        className="min-h-screen flex flex-col">
        <header className={`h-[68px] px-6 lg:px-8 ${visual.header} backdrop-blur-xl border-b border-slate-200/80 sticky top-0 z-30 flex items-center justify-between gap-5 shadow-[0_1px_0_rgba(15,23,42,0.02)]`}>
          <GlobalSearch />
          <div className="flex items-center gap-3 shrink-0">
            <button onClick={() => navigate("/workspace")} data-testid="workspace-switcher"
              className="hidden md:inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:border-slate-400 transition-colors">
              <CompanyMark company={workspace?.company} className="h-7 w-7" />
              <span className="max-w-48 truncate" title={workspace?.company?.name}>{workspace?.company?.name}</span>
              <span className="font-mono text-slate-400">{workspace?.financial_year}</span>
            </button>
            <button data-testid="language-toggle" onClick={toggle}
              className="inline-flex h-9 items-center gap-1.5 text-xs font-semibold px-3 rounded-lg border border-slate-200 bg-white hover:border-teal-300 hover:text-teal-800 transition-colors">
              <Languages className="h-3.5 w-3.5" />
              {lang === "en" ? "EN" : "हिं"}
            </button>
            <div className="h-8 w-px bg-slate-200" />
            <div className="text-right hidden lg:block">
              <p className="text-xs font-semibold text-slate-800">{profile?.trade_name || workspace?.company?.name || "Company"}</p>
              <p className="text-[10px] text-slate-500 font-mono mt-0.5">{profile?.gstin ? `GSTIN ${profile.gstin}` : "GSTIN not configured"}</p>
            </div>
            <span className="hidden xl:inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
              {user?.role}
            </span>
          </div>
        </header>

        <AnimatePresence mode="wait" initial={false}>
          <motion.main key={location.pathname}
            initial={reduceMotion ? false : shell === "prachi" ? { opacity: 0, x: 10 } : shell === "neo" ? { opacity: 0, scale: 0.992 } : { opacity: 0, y: 7 }}
            animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
            exit={reduceMotion ? undefined : shell === "prachi" ? { opacity: 0, x: -8 } : shell === "neo" ? { opacity: 0, scale: 0.996 } : { opacity: 0, y: -4 }}
            transition={{ duration: reduceMotion ? 0 : shell === "prachi" ? 0.2 : shell === "neo" ? 0.14 : 0.16, ease: [0.22, 1, 0.36, 1] }}
            className="p-6 lg:p-8 xl:p-9 max-w-[1500px] w-full mx-auto">
            <Outlet />
          </motion.main>
        </AnimatePresence>
      </div>
    </div>
  );
}
